import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';

import { asFilterValues, isSentenceFilter } from 'helpers/types/utils';
import { capitalize } from 'helpers/helpers';
import i18n, { translateBlock } from 'i18n';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { translateDateFilterValue } from 'routes/Dashboard/components/WidgetsLayout/PrintableGrid/helpers/dateHelpers';
import { Box } from '@mui/material';
import { GetLayoutChartDataResponse } from 'components/ChartContainer/types';

type TimeFilters = GetLayoutChartDataResponse['timeFilters'];
type EffectiveMetric = TimeFilters[0]['filteredMetric'];

const getDateFilterSerializer = (t: TranslationT, filter: TimeFilters[0]) => {
    const sentenceFilter: SentenceFilterT = {
        field: filter.field,
        value: filter.values ? filter.values.join('|') : '',
        values: filter?.values ? asFilterValues(filter?.values) : null,
        operation: filter.operator,
        valueScale: filter.valueScale,
        type: filter.type,
        displayIndex: 0,
    };

    if (isSentenceFilter(sentenceFilter)) {
        try {
            return translateDateFilterValue(t, sentenceFilter);
        } catch (error) {
            console.error(error);
            return '';
        }
    } else {
        console.error('Not a valid sentence filter: ', sentenceFilter);
        return '';
    }
};

const TimeFilteredMetricGrid = ({ metric }: { metric: EffectiveMetric }) => {
    const { t } = useTranslation('sentence');
    const { field, aggregation } = metric;
    return (
        <Grid container>
            <Grid xs={3}>
                <Typography variant="body1" align="left">{`${i18n.t(
                    'timeFilterModal.metric',
                )} : `}</Typography>
            </Grid>
            <Grid xs={9}>
                <Typography
                    variant="body1"
                    color="primary"
                    align="left"
                >{`${capitalize(t(`metricAggregations.${aggregation}`))} ${t(
                    `aggregationLinkers.${aggregation}`,
                )} ${translateBlock(field)}`}</Typography>
            </Grid>
        </Grid>
    );
};

const TimeFiltersGrid = ({
    metricField,
    timeFilters,
}: {
    metricField: string;
    timeFilters: TimeFilters;
}) => {
    const { t } = useTranslation();
    const { t: sentenceTranslation } = useTranslation('sentence');
    return (
        <Grid container>
            <Grid xs={3}>
                <Typography>{`${t(
                    'timeFilterTooltip.filterOn',
                )} :`}</Typography>
            </Grid>
            <Grid xs={9}>
                {timeFilters.map((filter) => {
                    const serializedTimeFilter = getDateFilterSerializer(
                        sentenceTranslation,
                        filter,
                    );
                    return (
                        <Typography
                            key={`${metricField}-${filter.field}`}
                            variant="body1"
                            color="primary"
                            align="left"
                        >{`${capitalize(
                            translateBlock(filter.field),
                        )} ${serializedTimeFilter}`}</Typography>
                    );
                })}
            </Grid>
        </Grid>
    );
};

interface TimeFilterModalProps {
    timeFilters: TimeFilters;
    metric: EffectiveMetric;
}

const TimeFilterModalItem = ({ metric, timeFilters }: TimeFilterModalProps) => (
    <Box sx={{ p: '10px' }}>
        <TimeFilteredMetricGrid metric={metric} />
        <TimeFiltersGrid
            metricField={`${metric.field}-${metric.aggregation}`}
            timeFilters={timeFilters}
        />
    </Box>
);

export default TimeFilterModalItem;
